import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { GrowthContentAssetSelectorComponent } from '../../shared/design-system/growth-content-asset-selector.component';
import { GrowthSegmentSelectorComponent } from '../../shared/design-system/growth-segment-selector.component';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  GrowthAudienceNeedSignal,
  GrowthContentPerformanceScorecard,
  GrowthIntelligencePreferenceProfile,
  GrowthIntelligenceService,
  parseJsonRows
} from './growth-intelligence.service';
import { combineUtcDateAndTime, defaultCalendarFromUtc, defaultCalendarToUtc } from './growth-datetime.util';

interface PreferenceScoreRow {
  key: string;
  score: number;
}

@Component({
  standalone: true,
  selector: 'ain-growth-content-performance',
  imports: [
    AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent, JalaliDateInputComponent,
    GrowthSegmentSelectorComponent, GrowthContentAssetSelectorComponent,
    FormsModule, TableModule, ButtonModule
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.intelligence.content.title')" [description]="t('growth.intelligence.content.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.intelligence.content.help.title'" [descriptionKey]="'growth.intelligence.content.help.description'"
        [bulletKeys]="['growth.intelligence.content.help.bullets.scorecards', 'growth.intelligence.content.help.bullets.audience']" />

      <section class="card">
        <div class="grid">
          <ain-form-field [label]="t('growth.intelligence.content.fields.contentAsset')">
            <ain-growth-content-asset-selector [(ngModel)]="filters.contentAssetId" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.content.fields.segment')">
            <ain-growth-segment-selector [(ngModel)]="filters.segmentId" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.content.fields.periodStart')"><ain-date-input [(ngModel)]="filters.periodStartUtc" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.content.fields.periodEnd')"><ain-date-input [(ngModel)]="filters.periodEndUtc" /></ain-form-field>
        </div>
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.intelligence.content.actions.refreshScorecards')" (click)="refreshScorecards()"></button>
          <button pButton type="button" severity="secondary" [label]="t('growth.intelligence.content.actions.refreshProfile')" (click)="refreshProfile()"></button>
          <button pButton type="button" severity="secondary" [label]="t('growth.intelligence.content.actions.refreshNeeds')" (click)="refreshNeeds()"></button>
        </div>
      </section>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card">
          <div class="section-title"><h3>{{ t('growth.intelligence.content.sections.scorecards') }}</h3></div>
          @if (scorecards().length === 0) {
            <ain-state type="empty" [title]="t('growth.intelligence.content.emptyScorecards')" />
          } @else {
            <p-table [value]="scorecards()" [rows]="8" [paginator]="true">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.intelligence.content.columns.contentAsset') }}</th>
                  <th>{{ t('growth.intelligence.content.columns.views') }}</th>
                  <th>{{ t('growth.intelligence.content.columns.clicks') }}</th>
                  <th>{{ t('growth.intelligence.content.columns.leads') }}</th>
                  <th>{{ t('growth.intelligence.content.columns.conversion') }}</th>
                  <th>{{ t('growth.intelligence.content.columns.score') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.contentAssetId }}</td>
                  <td>{{ row.viewCount }}</td>
                  <td>{{ row.clickCount }}</td>
                  <td>{{ row.leadCount }}</td>
                  <td>{{ row.conversionRate }}</td>
                  <td>{{ row.score }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        <section class="card">
          <div class="section-title"><h3>{{ t('growth.intelligence.content.sections.preferenceProfile') }}</h3></div>
          @if (preferenceRows().length === 0) {
            <ain-state type="empty" [title]="t('growth.intelligence.content.emptyProfile')" />
          } @else {
            <p-table [value]="preferenceRows()">
              <ng-template pTemplate="header">
                <tr><th>{{ t('growth.intelligence.content.columns.preferenceKey') }}</th><th>{{ t('growth.intelligence.content.columns.score') }}</th></tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr><td>{{ row.key }}</td><td>{{ row.score }}</td></tr>
              </ng-template>
            </p-table>
          }
        </section>

        <section class="card">
          <div class="section-title"><h3>{{ t('growth.intelligence.content.sections.audienceNeeds') }}</h3></div>
          @if (audienceNeeds().length === 0) {
            <ain-state type="empty" [title]="t('growth.intelligence.content.emptyNeeds')" />
          } @else {
            <p-table [value]="audienceNeeds()" [rows]="8" [paginator]="true">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.intelligence.content.columns.needTag') }}</th>
                  <th>{{ t('growth.intelligence.content.columns.sourceType') }}</th>
                  <th>{{ t('growth.intelligence.content.columns.confidence') }}</th>
                  <th>{{ t('growth.intelligence.content.columns.segment') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.needTag }}</td>
                  <td>{{ row.sourceType }}</td>
                  <td>{{ row.confidence }}</td>
                  <td>{{ row.segmentId ?? '-' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
  `]
})
export class GrowthContentPerformanceComponent {
  private readonly service = inject(GrowthIntelligenceService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly scorecards = signal<GrowthContentPerformanceScorecard[]>([]);
  readonly preferenceRows = signal<PreferenceScoreRow[]>([]);
  readonly audienceNeeds = signal<GrowthAudienceNeedSignal[]>([]);

  filters = {
    contentAssetId: null as string | null,
    segmentId: null as string | null,
    periodStartUtc: defaultCalendarFromUtc(),
    periodEndUtc: defaultCalendarToUtc()
  };

  private periodPayload() {
    return {
      periodStartUtc: combineUtcDateAndTime(this.filters.periodStartUtc, '00:00')!,
      periodEndUtc: combineUtcDateAndTime(this.filters.periodEndUtc, '23:59')!
    };
  }

  refreshScorecards(): void {
    this.loading.set(true);
    this.service.refreshContentPerformance({
      ...this.periodPayload(),
      contentAssetId: this.filters.contentAssetId
    }).subscribe({
      next: rows => { this.scorecards.set(rows); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }

  refreshProfile(): void {
    this.loading.set(true);
    this.service.refreshPreferenceProfile({
      ...this.periodPayload(),
      segmentId: this.filters.segmentId
    }).subscribe({
      next: profile => {
        const contentScores = parseJsonRows<{ contentType?: string; score?: number }>(profile.contentTypeScoresJson)
          .map(row => ({ key: row.contentType ?? '-', score: row.score ?? 0 }));
        const channelScores = parseJsonRows<{ channelKey?: string; score?: number }>(profile.channelScoresJson)
          .map(row => ({ key: row.channelKey ?? '-', score: row.score ?? 0 }));
        this.preferenceRows.set([...contentScores, ...channelScores]);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  refreshNeeds(): void {
    this.loading.set(true);
    this.service.refreshAudienceNeeds({
      ...this.periodPayload(),
      segmentId: this.filters.segmentId
    }).subscribe({
      next: rows => { this.audienceNeeds.set(rows); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }

  t(key: string): string { return this.i18n.translate(key); }
}
